import React from 'react'
import type { CSSProperties } from 'react'
import type { ObjectField } from '@puckeditor/core'
import './mobile-bg.scss'
import { ColorField } from '../ColorField'
import { FontFamilyField } from '../FontFamilyField'
import {
  FileUploadField,
  getPuckFileUrl,
  UploadedFile,
} from '../FileUploadField'

export type BackgroundStyleProps = {
  backgroundColor?: string
  backgroundImage?: UploadedFile | string
  mobileBackgroundImage?: UploadedFile | string
  backgroundSize?: string
  backgroundPosition?: string
  backgroundRepeat?: string
  backgroundImageOpacity?: number
  overflow?: string
}

export type BorderStyleProps = {
  borderColor?: string
  borderWidth?: string
  borderStyle?: string
  borderRadius?: string
  sizing?: '' | 'fit-content'
  /**
   * Which sides get the border, as a comma-separated list ('bottom',
   * 'top,bottom'). Empty or absent means all four — the behaviour before this
   * existed, so saved blocks are unaffected.
   */
  borderSides?: string
}

export type ShadowProps = {
  type?: 'none' | 'text-shadow' | 'drop-shadow' | 'box-shadow'
  x?: string
  y?: string
  blur?: string
  /** box-shadow only */
  spread?: string
  /** box-shadow only — draw the shadow inside the element */
  inset?: boolean
  color?: string
  opacity?: number
}

export type StyleProps = {
  background?: BackgroundStyleProps
  border?: BorderStyleProps
  shadow?: ShadowProps
}

export type FontWeight =
  | ''
  | '100'
  | '200'
  | '300'
  | '400'
  | '500'
  | '600'
  | '700'
  | '800'
  | '900'

export type TextTransform = '' | 'none' | 'uppercase' | 'lowercase' | 'capitalize'

export type TypographyProps = {
  fontFamily?: string
  fontFamilyFallback?: string
  textTransform?: TextTransform
}

export const textTransformOptions = [
  { label: 'Default', value: '' },
  { label: 'None', value: 'none' },
  { label: 'UPPERCASE', value: 'uppercase' },
  { label: 'lowercase', value: 'lowercase' },
  { label: 'Capitalize', value: 'capitalize' },
]

export const fontFamilyField = {
  type: 'custom' as const,
  label: 'Font Family',
  // read the field's own label so `{ ...fontFamilyField, label: 'X' }` works
  render: ({ value, onChange, id, field }: any) => (
    <FontFamilyField
      value={value as string | undefined}
      onChange={onChange}
      id={id}
      label={field?.label ?? 'Font Family'}
    />
  ),
}

export const fontFamilyFallbackField = {
  type: 'custom' as const,
  label: 'Font Family Fallback',
  render: ({ value, onChange, id, field }: any) => (
    <FontFamilyField
      value={value as string | undefined}
      onChange={onChange}
      id={id}
      label={field?.label ?? 'Font Family Fallback'}
    />
  ),
}

export const textTransformField = {
  type: 'select' as const,
  label: 'Text Transform',
  options: textTransformOptions,
}

export function applyTypography(t?: TypographyProps): CSSProperties {
  if (!t) return {}
  const css: CSSProperties = {}
  if (t.fontFamily) {
    css.fontFamily = t.fontFamilyFallback
      ? `${t.fontFamily}, ${t.fontFamilyFallback}`
      : t.fontFamily
  }
  if (t.textTransform) {
    css.textTransform = t.textTransform as CSSProperties['textTransform']
  }
  return css
}

export const fontWeightOptions = [
  { label: 'Default', value: '' },
  { label: 'Thin (100)', value: '100' },
  { label: 'Extra Light (200)', value: '200' },
  { label: 'Light (300)', value: '300' },
  { label: 'Regular (400)', value: '400' },
  { label: 'Medium (500)', value: '500' },
  { label: 'Semi Bold (600)', value: '600' },
  { label: 'Bold (700)', value: '700' },
  { label: 'Extra Bold (800)', value: '800' },
  { label: 'Black (900)', value: '900' },
]

export const backgroundStyleField: ObjectField<BackgroundStyleProps> = {
  type: 'object',
  objectFields: {
    backgroundColor: {
      label: 'Background Color',
      type: 'custom',
      render: ({ value, onChange, id }) => (
        <ColorField
          field={{ type: 'colorPicker', label: 'Background Color' }}
          value={value}
          onChange={onChange}
          id={id}
          clearable
          alpha
        />
      ),
    },
    backgroundImage: {
      label: 'Background Image',
      type: 'custom',
      render: ({ value, onChange }) => (
        <FileUploadField
          field={{ type: 'fileUpload', label: 'Background Image' }}
          value={value as UploadedFile | undefined}
          onChange={onChange}
        />
      ),
    },
    mobileBackgroundImage: {
      label: 'Mobile Background Image',
      type: 'custom',
      render: ({ value, onChange }) => (
        <FileUploadField
          field={{ type: 'fileUpload', label: 'Mobile Background Image' }}
          value={value as UploadedFile | undefined}
          onChange={onChange}
        />
      ),
    },
    backgroundSize: {
      label: 'Background Size',
      type: 'select',
      options: [
        { label: 'Cover (fill, crop edges)', value: 'cover' },
        { label: 'Contain (fit, may show gaps)', value: 'contain' },
        { label: 'Stretch (100% × 100%)', value: '100% 100%' },
        { label: '100% width', value: '100%' },
        { label: 'Auto (natural size)', value: 'auto' },
      ],
    },
    backgroundPosition: {
      label: 'Background Position',
      type: 'select',
      options: [
        { label: 'Center', value: 'center center' },
        { label: 'Top center', value: 'top center' },
        { label: 'Bottom center', value: 'bottom center' },
        { label: 'Left center', value: 'left center' },
        { label: 'Right center', value: 'right center' },
        { label: 'Top left', value: 'top left' },
        { label: 'Top right', value: 'top right' },
        { label: 'Bottom left', value: 'bottom left' },
        { label: 'Bottom right', value: 'bottom right' },
      ],
    },
    backgroundRepeat: {
      label: 'Background Repeat',
      type: 'select',
      options: [
        { label: 'No repeat', value: 'no-repeat' },
        { label: 'Repeat', value: 'repeat' },
        { label: 'Repeat X', value: 'repeat-x' },
        { label: 'Repeat Y', value: 'repeat-y' },
        { label: 'Space', value: 'space' },
        { label: 'Round', value: 'round' },
      ],
    },
    backgroundImageOpacity: {
      label: 'Background Image Opacity (0-1)',
      type: 'number',
      min: 0,
      max: 1,
    },
    overflow: {
      label: 'Overflow',
      type: 'select',
      options: [
        { label: 'Hidden', value: 'hidden' },
        { label: 'Visible', value: 'visible' },
        { label: 'Scroll', value: 'scroll' },
        { label: 'Auto', value: 'auto' },
        { label: 'Clip', value: 'clip' },
      ],
    },
  },
}

export const borderStyleField: ObjectField<BorderStyleProps> = {
  type: 'object',
  objectFields: {
    borderColor: {
      label: 'Border Color',
      type: 'custom',
      render: ({ value, onChange, id }) => (
        <ColorField
          field={{ type: 'colorPicker', label: 'Border Color' }}
          value={value}
          onChange={onChange}
          id={id}
          clearable
        />
      ),
    },
    borderWidth: {
      label: 'Border Width',
      type: 'text',
      placeholder: '1px',
    },
    borderStyle: {
      label: 'Border Style',
      type: 'select',
      options: [
        { label: 'auto', value: '' },
        { label: 'None', value: 'none' },
        { label: 'Solid', value: 'solid' },
        { label: 'Dashed', value: 'dashed' },
        { label: 'Dotted', value: 'dotted' },
        { label: 'Double', value: 'double' },
      ],
    },
    borderRadius: {
      label: 'Border Radius',
      type: 'text',
      placeholder: '0px',
    },
    borderSides: {
      label: 'Border Sides',
      type: 'select',
      options: [
        { label: 'All sides', value: '' },
        { label: 'Top only', value: 'top' },
        { label: 'Right only', value: 'right' },
        { label: 'Bottom only', value: 'bottom' },
        { label: 'Left only', value: 'left' },
        { label: 'Top & Bottom', value: 'top,bottom' },
        { label: 'Left & Right', value: 'left,right' },
      ],
    },
    sizing: {
      label: 'Width',
      type: 'radio',
      options: [
        { label: 'Block', value: '' },
        { label: 'Fit content', value: 'fit-content' },
      ],
    },
  },
}

export const shadowStyleField: ObjectField<ShadowProps> = {
  type: 'object',
  objectFields: {
    type: {
      label: 'Shadow Type',
      type: 'radio',
      options: [
        { label: 'None', value: 'none' },
        { label: 'Text Shadow', value: 'text-shadow' },
        { label: 'Drop Shadow', value: 'drop-shadow' },
        { label: 'Box Shadow', value: 'box-shadow' },
      ],
    },
    x: { label: 'Offset X', type: 'text', placeholder: '0px' },
    y: { label: 'Offset Y', type: 'text', placeholder: '2px' },
    blur: { label: 'Blur', type: 'text', placeholder: '4px' },
    spread: { label: 'Spread (box shadow)', type: 'text', placeholder: '0px' },
    inset: {
      label: 'Inset (box shadow)',
      type: 'radio',
      options: [
        { label: 'No', value: false },
        { label: 'Yes', value: true },
      ],
    },
    color: {
      label: 'Color',
      type: 'custom',
      render: ({ value, onChange, id }) => (
        <ColorField
          field={{ type: 'colorPicker', label: 'Shadow Color' }}
          value={value}
          onChange={onChange}
          id={id}
          clearable
        />
      ),
    },
    opacity: { label: 'Opacity (0-1)', type: 'number', min: 0, max: 1 },
  },
}

function hexToRgba(hex: string, opacity: number): string {
  const h = hex.replace('#', '')
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return hex
  const r = parseInt(full.slice(0, 2), 16)
  const g = parseInt(full.slice(2, 4), 16)
  const b = parseInt(full.slice(4, 6), 16)
  const a = Math.max(0, Math.min(1, opacity))
  return `rgba(${r}, ${g}, ${b}, ${a})`
}

export type BorderOverlayProps = {
  /** Any CSS filter value, e.g. the jagged url(data:image/svg+xml,...)#id. */
  filter?: string
  color?: string
  width?: string
  radius?: string
  margin?: string
}

/**
 * Hand-drawn/decorative border overlay (BR-5451/5452): an absolutely
 * positioned child carrying the border + an arbitrary CSS filter (SVG
 * turbulence data-uris etc). Replaces the ::before/::after customCss
 * pattern; the HOST element must be position:relative.
 */
export const borderOverlayField: ObjectField<BorderOverlayProps> = {
  type: 'object',
  label: 'Border Overlay (hand-drawn)',
  objectFields: {
    color: {
      label: 'Color',
      type: 'custom',
      render: ({ value, onChange, id }) => (
        <ColorField
          field={{ type: 'colorPicker', label: 'Color' }}
          value={value}
          onChange={onChange}
          id={id}
          clearable
        />
      ),
    },
    width: { label: 'Width', type: 'text', placeholder: '2px' },
    radius: {
      label: 'Radius',
      type: 'text',
      placeholder: '12px 15px 12px 18px / 18px 12px 15px 12px',
    },
    margin: { label: 'Margin', type: 'text', placeholder: '-1px' },
    filter: {
      label: 'CSS Filter (any value, e.g. jagged SVG url)',
      type: 'textarea',
    },
  },
}

/** Style for the overlay child; undefined when the overlay is not enabled
 * (color or filter set = enabled). */
export function borderOverlayStyle(
  p?: BorderOverlayProps,
): CSSProperties | undefined {
  if (!p || (!p.color && !p.filter)) return undefined
  return {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    zIndex: 2,
    border: `${p.width || '2px'} solid ${p.color || '#000000'}`,
    ...(p.radius ? { borderRadius: p.radius } : {}),
    ...(p.margin ? { margin: p.margin } : {}),
    ...(p.filter ? { filter: p.filter } : {}),
    transform: 'translateZ(0)',
  }
}

export function applyShadowStyle(shadow?: ShadowProps): CSSProperties {
  if (!shadow || !shadow.type || shadow.type === 'none') return {}
  const x = shadow.x || '0px'
  const y = shadow.y || '0px'
  const blur = shadow.blur || '0px'
  const opacity = typeof shadow.opacity === 'number' ? shadow.opacity : 1
  const color = shadow.color
    ? hexToRgba(shadow.color, opacity)
    : `rgba(0,0,0,${opacity})`
  const value = `${x} ${y} ${blur} ${color}`
  if (shadow.type === 'box-shadow') {
    const spread = shadow.spread || '0px'
    return {
      boxShadow: `${shadow.inset ? 'inset ' : ''}${x} ${y} ${blur} ${spread} ${color}`,
    }
  }
  if (shadow.type === 'drop-shadow') {
    return { filter: `drop-shadow(${value})` }
  }
  return { textShadow: value }
}

export function applyBackgroundStyle(bg?: BackgroundStyleProps): CSSProperties {
  if (!bg) return {}
  const css: CSSProperties = {}

  if (bg.backgroundColor) css.backgroundColor = bg.backgroundColor
  if (bg.overflow) css.overflow = bg.overflow as CSSProperties['overflow']

  const imageUrl = bg.backgroundImage
    ? getPuckFileUrl(bg.backgroundImage as UploadedFile)
    : undefined

  if (imageUrl) {
    css.backgroundImage = `url("${imageUrl}")`
    css.backgroundRepeat = bg.backgroundRepeat || 'no-repeat'
    css.backgroundSize = bg.backgroundSize || 'cover'
    css.backgroundPosition = bg.backgroundPosition || 'center center'

    const mobileUrl = bg.mobileBackgroundImage
      ? getPuckFileUrl(bg.mobileBackgroundImage as UploadedFile)
      : undefined
    if (mobileUrl) {
      ;(css as Record<string, unknown>)['--mobile-bg-image'] = `url("${mobileUrl}")`
    }
  }

  return css
}

/**
 * Split background into base + overlay layers when image opacity < 1.
 * Overlay must be rendered as an absolute-positioned child of the base element.
 * Base must be position:relative + overflow:hidden for overlay to clip correctly.
 */
export function splitBackgroundLayers(
  bg?: BackgroundStyleProps,
): { base: CSSProperties; overlay: CSSProperties | null } {
  if (!bg) return { base: {}, overlay: null }
  const opacity = bg.backgroundImageOpacity
  const hasOpacity =
    typeof opacity === 'number' && opacity >= 0 && opacity < 1
  const imageUrl = bg.backgroundImage
    ? getPuckFileUrl(bg.backgroundImage as UploadedFile)
    : undefined
  if (!hasOpacity || !imageUrl) {
    return { base: applyBackgroundStyle(bg), overlay: null }
  }
  const base: CSSProperties = {
    position: 'relative',
    isolation: 'isolate',
  }
  if (bg.backgroundColor) base.backgroundColor = bg.backgroundColor
  if (bg.overflow) base.overflow = bg.overflow as CSSProperties['overflow']
  const overlay: CSSProperties = {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    opacity,
    backgroundImage: `url("${imageUrl}")`,
    backgroundRepeat: bg.backgroundRepeat || 'no-repeat',
    backgroundSize: bg.backgroundSize || 'cover',
    backgroundPosition: bg.backgroundPosition || 'center center',
    zIndex: -1,
  }
  const mobileUrl = bg.mobileBackgroundImage
    ? getPuckFileUrl(bg.mobileBackgroundImage as UploadedFile)
    : undefined
  if (mobileUrl) {
    ;(overlay as Record<string, unknown>)['--mobile-bg-image'] = `url("${mobileUrl}")`
  }
  return { base, overlay }
}

const BORDER_SIDES = ['Top', 'Right', 'Bottom', 'Left'] as const

export function applyBorderStyle(border?: BorderStyleProps): CSSProperties {
  if (!border) return {}
  const css: CSSProperties = {}

  if (border.borderRadius) css.borderRadius = border.borderRadius
  if (border.sizing === 'fit-content') css.width = 'fit-content'

  const style = border.borderStyle
  let resolvedStyle: string | undefined
  if (style === 'none') {
    resolvedStyle = 'none'
  } else if (style && style !== '') {
    resolvedStyle = style
  } else if (border.borderColor || border.borderWidth) {
    resolvedStyle = 'solid'
  }

  const sides = (border.borderSides ?? '')
    .split(',')
    .map(s => s.trim().toLowerCase())
    .filter(Boolean)

  // No selection means all four sides — the original behaviour, so existing
  // saved blocks render identically.
  if (sides.length === 0) {
    if (border.borderColor) css.borderColor = border.borderColor
    if (border.borderWidth) css.borderWidth = border.borderWidth
    if (resolvedStyle) css.borderStyle = resolvedStyle as CSSProperties['borderStyle']
    return css
  }

  // Per-side longhands. The unselected sides are explicitly `none` rather than
  // simply omitted, so a border added on Default can be narrowed to one side at
  // a breakpoint without the other three leaking through.
  const on = new Set(sides)
  const target = css as Record<string, unknown>
  for (const side of BORDER_SIDES) {
    if (on.has(side.toLowerCase())) {
      if (border.borderColor) target[`border${side}Color`] = border.borderColor
      if (border.borderWidth) target[`border${side}Width`] = border.borderWidth
      target[`border${side}Style`] = resolvedStyle ?? 'solid'
    } else {
      target[`border${side}Style`] = 'none'
    }
  }

  return css
}

export function applyStyleProps(style?: StyleProps): CSSProperties {
  return {
    ...applyBackgroundStyle(style?.background),
    ...applyBorderStyle(style?.border),
    ...applyShadowStyle(style?.shadow),
  }
}
